import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LockKey, ArrowRight } from "@phosphor-icons/react";

const QUICK_LOGIN = [
  { label: "Admin",       email: "boaccntg@gmail.com",       password: "admin123" },
  { label: "Finance",     email: "finance@example.com",      password: "finance123" },
  { label: "Procurement", email: "procurement@example.com",  password: "procurement123" },
  { label: "Department",  email: "dept@example.com",         password: "dept123" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e, override) {
    if (e) e.preventDefault();
    const em = override?.email || email;
    const pw = override?.password || password;
    setError("");
    setLoading(true);
    try {
      const user = await login(em, pw);
      toast.success(`Welcome back, ${user.name}`);
      nav("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-background">
      {/* Left branding pane — Security Console */}
      <div className="sec-console sec-grid hidden lg:flex flex-col justify-between p-12 border-r border-[hsl(var(--console-border))] relative overflow-hidden">
        {/* Corner brackets */}
        <div className="pointer-events-none absolute inset-6">
          <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-[hsl(var(--console-glow)/0.55)]" />
          <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-[hsl(var(--console-glow)/0.55)]" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-[hsl(var(--console-glow)/0.55)]" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-[hsl(var(--console-glow)/0.55)]" />
        </div>
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="sec-brackets w-10 h-10 flex items-center justify-center font-heading font-black text-[hsl(var(--console-glow))] bg-[hsl(var(--console-elev))] border border-[hsl(var(--console-glow)/0.35)]">C</div>
            <div>
              <div className="font-heading font-black tracking-tight text-lg leading-none text-[hsl(var(--console-fg))]">CASEDIST INC.</div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <span className="sec-armed-dot" />
                <span className="sec-tag">Secure · Ops Console</span>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-md"
        >
          <div className="sec-tag mb-4">Operations · Budget · Procurement</div>
          <h1 className="font-heading font-black text-4xl xl:text-5xl leading-[1.05] tracking-tight text-[hsl(var(--console-fg))]">
            Budget to billing, <span className="text-[hsl(var(--console-glow))]">secured</span> in one clean workflow.
          </h1>
          <p className="mt-5 text-sm text-[hsl(var(--console-fg)/0.72)] leading-relaxed">
            Track requests, approvals, quotations, purchase orders and payments across
            departments — with permit versioning and auto-generated PDF &amp; Word exports.
          </p>

          {/* Tactical status strip */}
          <div className="mt-8 grid grid-cols-3 gap-3">
            {[
              { k: "Sessions", v: "ACTIVE" },
              { k: "Audit Log", v: "ARMED" },
              { k: "Backups",   v: "NOMINAL" },
            ].map((s) => (
              <div key={s.k} className="border border-[hsl(var(--console-border))] bg-[hsl(var(--console-elev))] px-3 py-2">
                <div className="sec-tag sec-tag-dim">{s.k}</div>
                <div className="flex items-center gap-1.5 mt-1">
                  <span className="sec-armed-dot" />
                  <span className="sec-tag">{s.v}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <div className="relative sec-tag sec-tag-dim">
          v1.0 · Casedist Inc. · Forest Console
        </div>
      </div>

      {/* Right form pane */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Sign In</div>
            <h2 className="font-heading font-black text-3xl tracking-tight">Enter your workspace</h2>
            <p className="text-sm text-muted-foreground mt-2">Use your issued credentials to access the operations console.</p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                id="password" type="password" autoComplete="current-password" required
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                placeholder="••••••••"
              />
            </div>

            {error ? (
              <div className="text-xs text-rose-600 border border-rose-200 bg-rose-50 rounded-md px-3 py-2" data-testid="login-error">{error}</div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full mt-2 h-10 font-medium"
            >
              <LockKey size={16} className="mr-2" />
              {loading ? "Signing in..." : "Sign in"}
              {!loading && <ArrowRight size={16} className="ml-2" />}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-border">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-3">Quick access · demo roles</div>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_LOGIN.map((q) => (
                <button
                  key={q.label}
                  onClick={() => submit(null, q)}
                  disabled={loading}
                  data-testid={`quick-login-${q.label.toLowerCase()}`}
                  className="text-left border border-border hover:border-foreground rounded-md px-3 py-2 transition-colors"
                >
                  <div className="text-xs font-medium">{q.label}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{q.email}</div>
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
